import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import { system } from 'api/client';

const GOOGLE_PLACES_API_KEY = process.env[`REACT_APP_GOOGLE_PLACES_API_KEY_${system.toUpperCase()}`];

let placesLibPromise = null;
let autocompleteService = null;
let autocompleteSessionToken = null;
let placesService = null;
let mapDomRef = null;

const CALLBACK_NAME = '__googleMapsPlacesReady';

function loadPlacesLibrary() {
  if (placesLibPromise) return placesLibPromise;

  placesLibPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve(window.google.maps.places);
      return;
    }

    window[CALLBACK_NAME] = () => {
      delete window[CALLBACK_NAME];
      resolve(window.google.maps.places);
    };

    if (!document.querySelector('#google-maps')) {
      const script = document.createElement('script');
      script.id = 'google-maps';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places&loading=async&callback=${CALLBACK_NAME}`;
      script.async = true;
      script.addEventListener('error', (e) => { delete window[CALLBACK_NAME]; reject(e); }, { once: true });
      document.head.appendChild(script);
    }
  });

  return placesLibPromise;
}

export default function AutocompleteGooglePlaces(props) {
  const [placesLib, setPlacesLib] = React.useState(null);
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);

  React.useEffect(() => {
    loadPlacesLibrary()
      .then(lib => setPlacesLib(lib))
      .catch(console.error);
  }, []);

  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.getPlacePredictions(request, callback);
      }, 400),
    [],
  );

  const getPlaceDetails = async (prediction) => {
    return new Promise((resolve) => {
      placesService.getDetails({
        placeId: prediction.place_id,
        language: 'iw',
        sessionToken: autocompleteSessionToken,
      }, (result, status) => {
        const addressDetails = { StreetNum: '', StreetName: '', CityName: '', Country: '' };

        if (status === 'OK') {
          for (const comp of result.address_components) {
            if (comp.types.includes('street_number')) addressDetails.StreetNum = comp.long_name;
            if (comp.types.includes('route')) addressDetails.StreetName = comp.long_name;
            if (comp.types.includes('locality')) addressDetails.CityName = comp.long_name;
            if (comp.types.includes('administrative_area_level_2') && !addressDetails.CityName) addressDetails.CityName = comp.long_name;
            if (comp.types.includes('administrative_area_level_1') && !addressDetails.CityName) addressDetails.CityName = comp.long_name;
            if (comp.types.includes('country')) addressDetails.Country = comp.long_name;
          }
        }

        resolve({ placeServiceResult: result, placeServiceStatus: status, addressDetails });
      });
    });
  };

  React.useEffect(() => {
    let active = true;

    if (!placesLib) return undefined;

    if (!autocompleteService) {
      mapDomRef = document.createElement('div');
      autocompleteSessionToken = new placesLib.AutocompleteSessionToken();
      autocompleteService = new placesLib.AutocompleteService();
      placesService = new placesLib.PlacesService(mapDomRef);
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({
      input: inputValue,
      language: 'iw',
      componentRestrictions: { country: 'il' },
      sessionToken: autocompleteSessionToken,
      types: ['geocode'],
    }, (results) => {
      if (active) {
        let newOptions = value ? [value] : [];
        if (results) newOptions = [...newOptions, ...results];
        setOptions(newOptions);
      }
    });

    return () => { active = false; };
  }, [value, inputValue, fetch, placesLib]);

  return (
    <Autocomplete
      fullWidth={true}
      id={props.id}
      name={props.name}
      disableClearable={true}
      disabled={props.disabled}
      noOptionsText={props.noOptionsText}
      onBlur={props.onBlur}
      className={props.className}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={async (event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue.place_id) {
          const placeDetails = await getPlaceDetails(newValue);
          props.onPlaceSelected(placeDetails);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.inputLabel}
          placeholder={props.inputPlaceholder}
          error={props.inputError}
          helperText={props.inputHelperText}
          fullWidth
        />
      )}
      renderOption={(optionProps, option) => {
        const matches = option.structured_formatting.main_text_matched_substrings || [];
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <li {...optionProps}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
